import TextArea from '@atlaskit/textarea';
import Button from '@atlaskit/button/standard-button';
import ModalDialog, {
  ModalTransition,
  ModalBody,
  ModalTitle,
  ModalHeader,
  ModalFooter,
} from '@atlaskit/modal-dialog';

import React from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import config from 'appconfig';
import t from 'modules/i18n/intl';
import { fetchDeveloperLicense } from 'modules/mac/duck/actions/license';
import { selectDeveloperLicense } from 'modules/mac/duck/selectors/license';
import { State } from 'model/State';
import { removeTimezone } from 'modules/mac/common/utils';

const strong = (chunks) => <strong>{chunks}</strong>;

const contactUs = (chunks) => <a href={config.companyContact}>{chunks}</a>;

const Dialog = ({ close, view, developerLicense, endDate, isViewButtonDisabled }) => (
  <ModalDialog autoFocus shouldCloseOnEscapePress width="medium">
    <ModalHeader>
      <ModalTitle>
        {developerLicense ? (
          <FormattedMessage
            id="mac.licenses.developer-license-modal.title.license-key"
            defaultMessage="License Key"
          />
        ) : (
          <FormattedMessage
            id="mac.licenses.developer-license-modal.title.developer-license"
            defaultMessage="Developer License"
          />
        )}
      </ModalTitle>
    </ModalHeader>

    <ModalBody>
      {developerLicense ? (
        <>
          <p>{developerLicense.licenseDetails.description}</p>
          <p>{developerLicense.licenseDetails.organisation}</p>
          <p>
            <TextArea
              cols={66}
              rows={15}
              maxHeight="30vh"
              readOnly
              value={developerLicense.licenseDetails.license}
              onClick={(e) => {
                e.currentTarget.select();
              }}
            />
          </p>
        </>
      ) : (
        <>
          <p>
            {t(
              {
                id: 'mac.licenses.developer-license-modal.description.part-1',
                defaultMessage:
                  'Developer licenses can be used by existing license holders who wish to deploy non-production installations of our software to assist with development and testing efforts. Developer licenses can also be used for non-production installations of our software deployed on a <strong>cold</strong> stand-by server.',
              },
              {
                strong,
              },
            )}
          </p>
          <p>
            <FormattedMessage
              id="mac.licenses.developer-license-modal.description.part-2"
              defaultMessage="
        This license is provided free of charge and the maintenance expiration of this license will be synchronized to the maintenance expiration date of your product ({endDate})"
              values={{
                endDate: (
                  <FormattedDate
                    value={removeTimezone(endDate)}
                    day="2-digit"
                    month="long"
                    year="numeric"
                  />
                ),
              }}
            />
          </p>
          <p>
            {t(
              {
                id: 'mac.licenses.developer-license-modal.description.part-3',
                defaultMessage:
                  'If you have any questions about the usage of this license, please <contactUs>contact us</contactUs>',
              },
              {
                contactUs,
              },
            )}
          </p>
        </>
      )}
    </ModalBody>
    <ModalFooter>
      <Button
        onClick={developerLicense ? close : view}
        appearance="primary"
        isDisabled={developerLicense ? false : isViewButtonDisabled}
        data-cy="actions-section-license-key-dialog-primary-button"
      >
        {developerLicense ? (
          <FormattedMessage
            id="mac.licenses.developer-license-modal.button.close"
            defaultMessage="Close"
          />
        ) : (
          <FormattedMessage
            id="mac.licenses.developer-license-modal.button.view"
            defaultMessage="View"
          />
        )}
      </Button>
      {!developerLicense && (
        <Button onClick={close} appearance="subtle">
          <FormattedMessage
            id="mac.licenses.developer-license-modal.button.cancel"
            defaultMessage="Cancel"
          />
        </Button>
      )}
    </ModalFooter>
  </ModalDialog>
);

const ViewDeveloperLicenseModalDialog = ({
  id,
  isModalOpened,
  setIsModalOpened,
  endDate,
  isActive,
  productType,
}) => {
  const dispatch = useDispatch();
  const developerLicense = useSelector((state) => selectDeveloperLicense(state as State, id));
  const isViewButtonDisabled = !(isActive && productType !== 'TRAINING');

  const onClose = () => {
    setIsModalOpened(false);
  };

  const onViewClick = () => {
    if (!isViewButtonDisabled) {
      dispatch(fetchDeveloperLicense(id));
    }
  };

  return (
    <ModalTransition>
      {isModalOpened && (
        <Dialog
          close={onClose}
          view={onViewClick}
          developerLicense={developerLicense}
          endDate={endDate}
          isViewButtonDisabled={isViewButtonDisabled}
        />
      )}
    </ModalTransition>
  );
};

export default ViewDeveloperLicenseModalDialog;
